/**
 * PeakTimeReminder — daily re-engagement toast that fires once per user
 * per day in the local evening rush window (18:00–20:00). Pulls the
 * creator's most recent uploaded video and its inflated view count so
 * the copy is personal ("Your last video hit 12.4K views…") rather than
 * generic marketing spam.
 *
 * Storage:
 *   localStorage `neosync_peak_reminder_last` = 'YYYY-MM-DD' → last date shown.
 *   localStorage `neosync_peak_reminder_dismissed` = true → user tapped the
 *     'X' or 'Not now' — respect that; stop showing for 7 days.
 *
 * Trigger paths:
 *   • Component mount (once, when user first hits the app in the window)
 *   • 15-minute interval check while the tab is open (catches the exact
 *     moment we cross 18:00 during a long session)
 *   • Window focus event (foreground → re-check)
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const LS_LAST_KEY = "neosync_peak_reminder_last";
const LS_DISMISS_KEY = "neosync_peak_reminder_dismissed_until";
const PEAK_START = 18; // 6pm local
const PEAK_END = 20;   // 8pm local

const _fmt = (n) => {
  n = Number(n) || 0;
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
};

const _isoDay = (d = new Date()) => d.toISOString().slice(0, 10);

export default function PeakTimeReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const firedThisSession = useRef(false);

  useEffect(() => {
    if (!user?.username) return;

    const tryFire = async () => {
      if (firedThisSession.current) return;

      const now = new Date();
      const hour = now.getHours();
      if (hour < PEAK_START || hour >= PEAK_END) return;

      const today = _isoDay(now);
      const lastShown = localStorage.getItem(LS_LAST_KEY);
      if (lastShown === today) return;

      // Respect a snooze — user tapped "Not now" gets 7 days off.
      const dismissedUntil = localStorage.getItem(LS_DISMISS_KEY);
      if (dismissedUntil && new Date(dismissedUntil) > now) return;

      // Fetch their most recent video for personalized copy.
      let vid = null;
      try {
        const r = await api.get(
          `/users/${encodeURIComponent(user.username)}/videos?limit=1`,
        );
        vid = Array.isArray(r.data) ? r.data[0] : null;
      } catch {
        /* No videos yet — the reminder isn't useful for accounts that
           haven't uploaded, so bail. */
      }
      if (!vid) return;

      firedThisSession.current = true;
      localStorage.setItem(LS_LAST_KEY, today);

      const views = _fmt(vid.displayed_view_count ?? vid.view_count ?? 0);
      toast(
        (t) => (
          <div className="flex items-start gap-3">
            <div className="mt-0.5 shrink-0 h-8 w-8 rounded-full bg-[#FF007F]/15 border border-[#FF007F]/50 flex items-center justify-center text-[#FF007F]">
              <TrendingUp className="h-4 w-4" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <p className="text-white font-mono text-[11px] leading-tight">
                Your last video hit{" "}
                <span className="text-[#39FF14] font-bold">{views}</span> views.
                <br />
                Post another to catch the evening rush.
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  data-testid="peak-reminder-upload"
                  onClick={() => {
                    toast.dismiss(t);
                    navigate("/upload");
                  }}
                  className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-black bg-[#39FF14] hover:brightness-110"
                >
                  post now
                </button>
                <button
                  type="button"
                  data-testid="peak-reminder-later"
                  onClick={() => {
                    // 7-day snooze so we don't nag the same user every day.
                    const in7 = new Date(Date.now() + 7 * 24 * 3600 * 1000);
                    localStorage.setItem(LS_DISMISS_KEY, in7.toISOString());
                    toast.dismiss(t);
                  }}
                  className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-white/60 border border-white/15 hover:text-white"
                >
                  not now
                </button>
              </div>
            </div>
          </div>
        ),
        {
          duration: 12000,
          id: "peak-time-reminder",
        },
      );
    };

    // Fire once immediately.
    tryFire();
    // Re-check every 15 min in case the tab has been open all afternoon.
    const iv = setInterval(tryFire, 15 * 60 * 1000);
    // Also on focus so returning from another app triggers a check.
    const onFocus = () => tryFire();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, [user?.username, navigate]);

  return null;
}
